import { AxiosError, AxiosResponse } from 'axios';
import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';

export const optionSchema = z.object({
  value: z.string().readonly(),
  label: z.string().readonly(),
});

export const optionWithIdSchema = optionSchema.extend({
  id: z.string().readonly(),
});

export const optionNumberSchema = z.object({
  value: z.number().readonly(),
  label: z.string().readonly(),
});

export const optionNumberSchemaRequired = z.object({
  value: z.number().min(1, { message: ERROR_MESSAGES.REQUIRED }).readonly(),
  label: z.string().readonly(),
});

export const listingSubspecialtySchema = z.object({
  areaOfInterestId: z.number(),
  aoiName: z.string(),
});

export type OptionType = z.infer<typeof optionSchema>;
export type OptionWithIdType = z.infer<typeof optionWithIdSchema>;
export type OptionNumberType = z.infer<typeof optionNumberSchema>;

export const optionSchemaRequired = z.object({
  value: z.string().min(1, { message: ERROR_MESSAGES.REQUIRED }).readonly(),
  label: z.string().readonly(),
});

export type Subset<K> = {
  [attr in keyof K]?: K[attr] extends object ? Subset<K[attr]> : K[attr];
};

// Option fields parsed from documents (AI import) can arrive null or as an
// empty option; bare optionSchema then fails with Zod's default
// "Invalid input" message (MCV-1466). Normalize to an empty option and
// validate the value so the user sees which field is actually missing.
export const requiredOptionSchema = (message: string) =>
  z.object(
    {
      value: z.string().min(1, { message }).readonly(),
      label: z.string().readonly(),
    },
    { error: message }
  );

export const addressSchema = z.object({
  addressType: optionSchema.nullish(),
  addressLine1: z
    .string()
    .min(1, { message: 'Address is required' })
    .max(100, { message: 'Address must be 100 characters or fewer' }),
  addressLine2: z.string().max(100, { message: 'Must be 100 characters or fewer' }).nullish(),
  city: z
    .string()
    .min(1, { message: 'City is required' })
    .max(50, { message: 'City must be 50 characters or fewer' }),
  stateCode: requiredOptionSchema('State is required'),
  postalCode: z
    .string()
    .min(1, { message: 'Postal code is required' })
    .min(5, { message: 'Postal code must be at least 5 characters' })
    .max(10, { message: 'Postal code must be 10 characters or fewer' }),
  countryCode: requiredOptionSchema('Country is required'),
});

export const addressSchemaOptional = z.object({
  addressType: optionSchema.nullish(),
  addressLine1: z.string(),
  addressLine2: z.string().nullish(),
  city: z.string(),
  stateCode: optionSchema,
  postalCode: z.string().refine((val) => val.length >= 5 || val.length === 0, {
    message: 'Postal code must be at least 5 characters',
  }),
  countryCode: optionSchema,
});

export type AddressFormValues = z.infer<typeof addressSchema>;

export const facilitySchema = z.object({
  label: z.string().min(1, { message: 'Facility Name is required' }),
  // Original API name, before the picker adds its location suffix.
  rawName: z.string().optional(),
  value: z.string(),
  userAdded: z.boolean().optional(),
  address: addressSchema,
});

export const facilitySchemaOptional = z
  .object({
    label: z.string(),
    rawName: z.string().optional(),
    value: z.string(),
    userAdded: z.boolean(),
    address: addressSchemaOptional,
  })
  .partial();

export const facilitySchemaRequiredNoAddress = z
  .object({
    label: z.string().min(1, { message: 'Facility Name is required' }),
    rawName: z.string().optional(),
    value: z.string(),
    userAdded: z.boolean().optional(),
    address: addressSchemaOptional,
  })
  .superRefine((facility, ctx) => {
    if (!facility.userAdded && !facility.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Facility Name is required',
        path: ['value'],
      });
    }
  });

export type FacilityFormValues = z.infer<typeof facilitySchema>;

export type XStateAxiosError = { type: string; error: AxiosError; actorId: string };
export type XStateAxiosSuccess = { type: string; data: AxiosResponse; actorId: string };

export type CustomAlertMessagePayload = {
  text: string;
  subText: string;
};
